import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SendIcon from "@mui/icons-material/Send";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { ChatActionButton } from "../../../UIToolkit/Chat/ChatActionButton";
import type { MonthlyDayActionsApi } from "./useMonthlyDayActions";

type Props = {
	/** The shared action layer — see `useMonthlyDayActions`. Both surfaces open the identical panel. */
	actions: MonthlyDayActionsApi;
	/**
	 * Whether each icon is its own tab stop. Pass `false` inside a grid cell: 42 cells must not become
	 * 126 tab stops, so the cell stays the single stop and reaches these actions by keyboard shortcut
	 * (see `MonthlyJobCell`'s header note). `true` in ordinary chrome, where tabbing is the whole point.
	 */
	focusable: boolean;
	/** Suffixed with `-add-nurse` / `-remove-staff` / `-post-shift`. */
	dataTestIdPrefix: string;
};

/**
 * The monthly view's three per-day actions — post a shift, add staff, remove staff — as one glyph row.
 *
 * Glyphs AND order come from `DayViewItem/DayPostShift/DayQuickActions`, the weekly view's day-header
 * quick actions: send, then person-add, then delete-outline. `MasterSchedule/v3/StaffingPeopleView` already
 * follows the same reference, so every place in the app that posts a shift or adds someone to the schedule
 * now looks and reads alike — the glyph is the label users learn, so it must not vary by screen. Size is
 * left to `ChatActionButton` (`ICON_SIZE.MICRO`) rather than a per-icon `fontSize`, because these sit
 * inside a micro action bar rather than in page chrome.
 *
 * Each one opens the app's single staffing-changes panel on its own tab; the panel is the same one the
 * weekly grid and the day view use, so this screen adds no surface of its own. Purely presentational:
 * every decision (whether the actions may run, which tab opens, what it prefills) belongs to
 * `useMonthlyDayActions`, and this renders the same three glyphs in the same order for the day cell and
 * the day sidebar. Two surfaces, one component, so their guards cannot drift apart.
 *
 * It renders the BUTTONS only. The host owns the `ChatActionBar` around them (a cell reveals that bar on
 * hover; a sidebar header keeps it visible at rest) and must also render `actions.overlays` OUTSIDE that
 * bar — a hidden bar is `opacity: 0; pointer-events: none`, which is no place to park the mount point of
 * a panel.
 *
 * Each icon stops its click from propagating: it sits inside a clickable day cell, which would otherwise
 * also open the day sidebar behind the panel the user just asked for.
 */
export const MonthlyDayActions: FC<Props> = ({ actions, focusable, dataTestIdPrefix }) => {
	const { t } = useTranslation();
	const { canAct, disabledReason, offersPost, canPost, openPanel } = actions;
	// Only ever `-1` or unset: a positive index would fight document order.
	const tabIndex = focusable ? undefined : -1;
	// A disabled button is inert, so the tooltip has to carry the reason rather than the action's name.
	const tooltip = disabledReason ?? undefined;

	return (
		<>
			{/* Posting is the one action a department can be configured never to offer at all (an
			    auto-scheduler department plans its own shifts), so there it is dropped from the row rather
			    than shown permanently dead — a control that can never do anything is noise, not information.
			    A post that is merely unavailable RIGHT NOW (past day, draft mode, unloaded day) still renders
			    disabled with `disabledReason` as its tooltip, like its two neighbours. */}
			{offersPost && (
				<ChatActionButton
					label={t("monthly.view.post.shift")}
					tooltip={tooltip}
					icon={<SendIcon />}
					disabled={!canPost}
					tabIndex={tabIndex}
					onClick={(event) => {
						event.stopPropagation();
						openPanel("create-shift");
					}}
					dataTestId={`${dataTestIdPrefix}-post-shift`}
				/>
			)}
			<ChatActionButton
				label={t("add.staff")}
				tooltip={tooltip}
				icon={<PersonAddIcon />}
				disabled={!canAct}
				tabIndex={tabIndex}
				onClick={(event) => {
					event.stopPropagation();
					openPanel("add-nurse");
				}}
				dataTestId={`${dataTestIdPrefix}-add-nurse`}
			/>
			<ChatActionButton
				label={t("monthly.view.remove.staff")}
				tooltip={tooltip}
				icon={<DeleteOutlineIcon />}
				disabled={!canAct}
				tabIndex={tabIndex}
				onClick={(event) => {
					event.stopPropagation();
					openPanel("remove-nurse");
				}}
				dataTestId={`${dataTestIdPrefix}-remove-staff`}
			/>
		</>
	);
};
